import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector, TypedUseSelectorHook } from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistStore,
  persistReducer,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { rootReducer } from "./root.reducer";
import { axiosMiddleware } from "@/middleware/axios.middleware";

/**
 * Redux persist configuration
 * Persists the entire store to localStorage
 */
const persistConfig = {
  key: "root",
  version: 1,
  storage,
  // Optionally blacklist certain reducers from persistence
  // blacklist: ['someTemporaryState']
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

/**
 * Configure the Redux store with persistence and middleware
 */
export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== "production" && {
    trace: true,
    traceLimit: 25,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([axiosMiddleware]),
});

export const persistor = persistStore(store);

// Export types for TypeScript support
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Export typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
